import { useSortable } from '@dnd-kit/sortable';
import { ColumnDef, SortingFn } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { PosDropdown, PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useOffices } from 'src/hooks/api/useOffices';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { BroadcastIcon } from 'src/svgs/BroadcastIcon';
import { FlattenedViewIcon } from 'src/svgs/FlattenedViewIcon';
import { IconsFill, MoreIcon } from 'src/svgs/Viagogo';
import { TableShimmeringDiv } from 'src/tables/Table';
import { ContentId } from 'src/utils/constants/contentId';
import { getDeliveryTypeIcon } from 'src/utils/ticketTypeUtils';
import { DeliveryType, Feature } from 'src/WebApiController';

import { RankPremiumType } from '../../GroupListingsV2.types';
import { GroupListingTableItem } from './GroupListingsPreviewTable';

export const UngroupedListingGroupId = 'UngroupedListingGroup';

export enum GroupListingTableColumnId {
  Id = 'id',
  Priority = 'priority',
  Section = 'section',
  Row = 'row',
  Seats = 'seats',
  Premium = 'premium',
  DeliveryType = 'deliveryType',
  AvailableQuantity = 'availableQuantity',
  UnitCost = 'unitCost',
  Office = 'office',
  Actions = 'actions',
}

const DeliveryTypeCell = ({ deliveryType }: { deliveryType: DeliveryType }) => {
  const icon = getDeliveryTypeIcon(deliveryType, null, IconsFill.textStrong);
  return icon;
};

// Cell Component
const RowDragHandleCell = ({ rowId }: { rowId: number }) => {
  const { attributes, listeners } = useSortable({
    id: rowId,
  });
  return (
    <FlattenedViewIcon
      {...attributes}
      {...listeners}
      fill={IconsFill.currentColor}
    />
  );
};

// We don't sort the table using the built-in sorting mechanism,
// rather we sort the data in the onSortingChange callback
const dummySortingFn: SortingFn<GroupListingTableItem | null> = (_, b) => 0;

export const usePreviewTableColumns = (
  desiredActiveListings: number | null,
  isUngroupedGroup: boolean,
  removeListing: (listingId: number) => void,
  onMoveListing: (listingId: number) => void,
  enableSorting = false
) => {
  const hasEnableOffice = useUserHasFeature(Feature.EnableOffice);
  const columns = useMemo((): ColumnDef<GroupListingTableItem | null>[] => {
    const tableColumns: ColumnDef<GroupListingTableItem | null>[] = [
      {
        id: GroupListingTableColumnId.Id,
        header: () => undefined,
        size: 40,
        cell: ({ row: { original } }) => {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          return <RowDragHandleCell rowId={original.id} />;
        },
        enableSorting: false,
      },
      {
        id: GroupListingTableColumnId.Priority,
        header: () => (
          <TableHeaderCellDiv align="center" showTooltip={false}>
            #
          </TableHeaderCellDiv>
        ),
        size: 40,
        accessorFn: (data) => data?.priority ?? 0,
        cell: ({ row: { original, depth }, getValue }) => {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          const priority = getValue<number>();

          if (depth > 0) return null;

          if (desiredActiveListings && priority <= desiredActiveListings) {
            return (
              <TableCellDiv align="center" showTooltip={false}>
                <BroadcastIcon size={vars.iconSize.m} />
              </TableCellDiv>
            );
          }

          return (
            <TableCellDiv align="center" showTooltip={false}>
              {priority}
            </TableCellDiv>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: GroupListingTableColumnId.Section,
        header: () => (
          <TableHeaderCellDiv showTooltip={false}>
            <Content id={ContentId.Section} />
          </TableHeaderCellDiv>
        ),
        minSize: 50,
        maxSize: 100,
        accessorFn: (data) => data?.seating.section,
        cell: ({ row: { original } }) => {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          return (
            <TableCellDiv showTooltip={false}>
              {original.seating.section}
            </TableCellDiv>
          );
        },
        enableColumnFilter: false,
        enableSorting,
        sortingFn: dummySortingFn,
      },
      {
        id: GroupListingTableColumnId.Row,
        header: () => (
          <TableHeaderCellDiv>
            <Content id={ContentId.Row} />
          </TableHeaderCellDiv>
        ),
        minSize: 50,
        maxSize: 70,
        accessorFn: (data) => data?.seating.row ?? '',
        cell: ({ row: { original } }) => {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          return (
            <TableCellDiv showTooltip={false}>
              {original.seating.row}
            </TableCellDiv>
          );
        },
        enableColumnFilter: false,
        enableSorting,
        sortingFn: dummySortingFn,
      },
      {
        id: GroupListingTableColumnId.Seats,
        header: () => (
          <TableHeaderCellDiv showTooltip={false}>
            <Content id={ContentId.Seats} />
          </TableHeaderCellDiv>
        ),
        minSize: 50,
        maxSize: 100,
        accessorFn: (data) => data?.seating,
        cell: ({ row: { original } }) => {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          let text = original.seating?.seatFr;
          if (
            original.seating?.seatTo &&
            original.seating.seatTo !== original.seating.seatFr
          ) {
            text += ` - ${original.seating.seatTo}`;
          }

          return <TableCellDiv showTooltip={false}>{text}</TableCellDiv>;
        },
        sortingFn: dummySortingFn,
        enableColumnFilter: false,
        enableSorting,
      },
      {
        id: GroupListingTableColumnId.Premium,
        header: () => (
          <TableHeaderCellDiv showTooltip={false}>
            <Content id={ContentId.RankPremium} />
          </TableHeaderCellDiv>
        ),
        minSize: 50,
        maxSize: 100,
        cell: ({ row: { original } }) => {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          const undercutSettings = original.undercutSetting;

          const rankPremium =
            undercutSettings?.undAbsAmt != null
              ? RankPremiumType.Absolute
              : undercutSettings?.undRelAmt != null
                ? RankPremiumType.Relative
                : undefined;

          const premiumValue =
            undercutSettings?.undAbsAmt != null
              ? undercutSettings?.undAbsAmt
              : undercutSettings?.undRelAmt != null
                ? undercutSettings?.undRelAmt * 100
                : undefined;

          const display =
            premiumValue == null
              ? undefined
              : rankPremium === RankPremiumType.Absolute
                ? `$${premiumValue}`
                : rankPremium === RankPremiumType.Relative
                  ? `${Math.round(premiumValue * 100) / 100}%`
                  : premiumValue;

          return <TableCellDiv showTooltip={false}>{display}</TableCellDiv>;
        },
        enableColumnFilter: false,
        enableSorting,
      },
      {
        id: GroupListingTableColumnId.DeliveryType,
        header: () => (
          <TableHeaderCellDiv align="center" showTooltip={false}>
            <Content id={ContentId.TicketType} />
          </TableHeaderCellDiv>
        ),
        minSize: 80,
        maxSize: 100,
        accessorFn: (data) => data?.delivType ?? 0,
        cell: ({ row: { original } }) => {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          return (
            <TableCellDiv align="center" showTooltip={false}>
              <DeliveryTypeCell deliveryType={original.delivType} />
            </TableCellDiv>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: GroupListingTableColumnId.AvailableQuantity,
        header: () => (
          <TableHeaderCellDiv showTooltip={false}>
            <Content id={ContentId.Sold} />/<Content id={ContentId.Unsold} />
          </TableHeaderCellDiv>
        ),
        minSize: 70,
        maxSize: 70,
        accessorFn: (data) => data?.availQty ?? 0,
        cell: function Cell({ row: { original } }) {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          const available = original.availQty ?? 0;
          const sold = original.ticketCnt - available;

          return (
            <TableCellDiv
              showTooltip={false}
            >{`${sold}/${available}`}</TableCellDiv>
          );
        },
        enableColumnFilter: false,
        enableSorting,
        sortingFn: dummySortingFn,
      },
      {
        id: GroupListingTableColumnId.UnitCost,
        header: () => (
          <TableHeaderCellDiv align="right" showTooltip={false}>
            <Content id={ContentId.UnitCost} />
          </TableHeaderCellDiv>
        ),
        minSize: 70,
        maxSize: 100,
        accessorFn: (data) => data?.unitCst?.disp ?? data?.faceValue?.disp ?? 0,
        accessorKey: 'unitCst',
        cell: function Cell({ row: { original } }) {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          return (
            <TableCellDiv align="right" showTooltip={false}>
              {original.unitCst?.disp ?? original?.faceValue?.disp ?? (
                <Content id={ContentId.NA} />
              )}
            </TableCellDiv>
          );
        },
        enableColumnFilter: false,
        enableSorting,
        sortingFn: dummySortingFn,
      },
      ...(hasEnableOffice
        ? [
            {
              id: GroupListingTableColumnId.Office,
              header: () => (
                <TableHeaderCellDiv showTooltip={false}>
                  Office
                </TableHeaderCellDiv>
              ),
              minSize: 60,
              maxSize: 80,
              accessorFn: (data: GroupListingTableItem | null) =>
                data?.officeId ?? '—',
              cell: function Cell({
                row: { original },
              }: {
                row: { original: GroupListingTableItem | null };
              }) {
                const { getOfficeName } = useOffices();

                if (!original) {
                  return <TableShimmeringDiv />;
                }

                const officeId = original.officeId;
                const officeName =
                  officeId != null ? getOfficeName(officeId) : null;

                return (
                  <TableCellDiv showTooltip={false}>{officeName}</TableCellDiv>
                );
              },
              enableColumnFilter: false,
              enableSorting: false,
            } as ColumnDef<GroupListingTableItem | null>,
          ]
        : []),
      {
        id: GroupListingTableColumnId.Actions,
        header: () => (
          <TableHeaderCellDiv align="right" showTooltip={false}>
            <Content id={ContentId.Actions} />
          </TableHeaderCellDiv>
        ),
        size: 40,
        cell: function Cell({ row: { original } }) {
          if (!original) {
            return <TableShimmeringDiv />;
          }

          return (
            <TableCellDiv align="right" showTooltip={false}>
              <PosDropdown
                key="event-action-dropdown"
                trigger={
                  <div
                    style={{
                      padding: `0 ${vars.spacing.sm} 0 ${vars.spacing.xxs}`,
                    }}
                  >
                    <MoreIcon
                      withHoverEffect
                      fill={IconsFill.textBrand}
                      align="middle"
                    />
                  </div>
                }
                align="end"
              >
                <PosDropdownItem
                  key="move"
                  onClick={() => onMoveListing(original.id)}
                >
                  <Content id={ContentId.MoveTo} />
                </PosDropdownItem>
                {!isUngroupedGroup && (
                  <PosDropdownItem
                    key="remove"
                    onClick={() => removeListing(original.id)}
                  >
                    <Content id={ContentId.RemoveFromGroup} />
                  </PosDropdownItem>
                )}
              </PosDropdown>
            </TableCellDiv>
          );
        },
        enableSorting: false,
      },
    ];

    return tableColumns.filter((column) => {
      if (isUngroupedGroup) {
        return (
          column.id &&
          ![
            GroupListingTableColumnId.Id,
            GroupListingTableColumnId.Premium,
          ].includes(column.id as GroupListingTableColumnId)
        );
      }
      return true;
    });
  }, [
    desiredActiveListings,
    enableSorting,
    hasEnableOffice,
    isUngroupedGroup,
    onMoveListing,
    removeListing,
  ]);

  return columns;
};
